// F23: caps concurrency at 5 via pAllSettled (backend chokes on 50+ parallel writes).
// V-32: single summary toast per call instead of one per failure. All-404 heuristic
// detects session expiry (backend Root.pm forwards unauthenticated to /default → 404)
// and dispatches fetchSession rather than showing 50 error toasts.

import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { fetcherFull, fetcherSimple } from "utils/api";
import { fetchSession } from "store/auth";

const DEFAULT_CONCURRENCY = 5;

// Run `mapper` over `items` in sequential batches of `concurrency`,
// collecting Promise.allSettled results. A batch must fully settle before
// the next batch starts, which is what enforces the cap.
async function pAllSettled(items, mapper, concurrency = DEFAULT_CONCURRENCY) {
    const results = [];
    for (let i = 0; i < items.length; i += concurrency) {
        const batch = items.slice(i, i + concurrency);
        results.push(...await Promise.allSettled(batch.map(mapper)));
    }
    return results;
}

function partition(results) {
    const succeeded = [];
    const failed = [];
    for (const r of results) {
        if (r.status === "fulfilled") succeeded.push(r.value);
        else failed.push(r.reason);
    }
    return { succeeded, failed };
}

// Session-expiry heuristic: every request failed with the same shape
// (status 404 + code "NOT_FOUND") that Root.pm's auto -> /default path
// produces when the session is gone. A single stale answer would also
// 404, but in a mixed batch the other requests would succeed; only when
// the entire batch returns this shape do we attribute it to auth loss.
function looksLikeSessionExpiry(succeeded, failed) {
    if (succeeded.length !== 0 || failed.length === 0) return false;
    return failed.every(
        (r) => r?.status === 404 && r?.code === "NOT_FOUND",
    );
}

// Emit one summary toast for the batch and log individual reasons to the
// console for debugging. Returns the toast decision so callers can react.
//
// infinitive: "apply theme to" / "remove theme from"
// pastTense:  "Applied theme to" / "Removed theme from"
function reportFailures({ succeeded, failed, infinitive, pastTense, logLabel }) {
    if (failed.length === 0) return { kind: "ok" };

    // Per-failure details go to the console; only the summary reaches the
    // user, so a 50-response batch never produces 50 toasts.
    console.warn(
        `[theme-assignment] ${failed.length} ${logLabel} failure(s):`,
        failed,
    );

    if (looksLikeSessionExpiry(succeeded, failed)) {
        toast.error("Session expired. Please log in again.");
        return { kind: "session" };
    }

    if (succeeded.length === 0) {
        toast.error(
            `Failed to ${infinitive} ${failed.length} response(s). See console for details.`,
        );
        return { kind: "all_failed" };
    }

    toast.warn(
        `${pastTense} ${succeeded.length} response(s); ${failed.length} failed. See console for details.`,
    );
    return { kind: "partial" };
}

export function useThemeAssignment({
    themesDataMutate,
    buildThemesDataMutate,
    customResponsesFilterMutate,
    invalidateSurveyCaches,
    setSelectedRows,
}) {
    const dispatch = useDispatch();

    const refreshAfterAssignment = () => {
        if (typeof themesDataMutate === "function") {
            themesDataMutate().catch((err) =>
                console.warn("Theme cache refresh failed:", err),
            );
        }
        if (typeof buildThemesDataMutate === "function") {
            buildThemesDataMutate().catch((err) =>
                console.warn("Build theme cache refresh failed:", err),
            );
        }
        if (typeof customResponsesFilterMutate === "function") {
            customResponsesFilterMutate().catch((err) =>
                console.warn("Response cache refresh failed:", err),
            );
        }
        if (typeof invalidateSurveyCaches === "function") {
            invalidateSurveyCaches();
        }
        if (typeof setSelectedRows === "function") {
            setSelectedRows([]);
        }
    };

    const addToTheme = async (selectedThemeIds, responsesToAdd) => {
        if (!responsesToAdd) {
            toast.warn("No responses to theme!");
            return { succeeded: [], failed: [] };
        }
        if (!selectedThemeIds) {
            toast.warn("No themes to add!");
            return { succeeded: [], failed: [] };
        }

        const items = [];
        responsesToAdd.forEach((response) => {
            const existingThemeIds = response.themes
                ? Object.keys(response.themes).map(Number)
                : [];
            const newThemeIds = selectedThemeIds.filter(
                (themeId) => !existingThemeIds.includes(themeId),
            );
            if (newThemeIds.length > 0) {
                const themesList = {
                    themes: [...existingThemeIds, ...newThemeIds],
                };
                const path = `/v2/response/answer/${response.answer_nanoid}/themes`;
                items.push({ path, themesList });
            }
        });

        if (items.length === 0) {
            toast.info("All selected responses already have these themes.");
            return { succeeded: [], failed: [] };
        }

        const results = await pAllSettled(
            items,
            (item) => fetcherFull(item.path, "PUT", item.themesList),
            DEFAULT_CONCURRENCY,
        );
        const { succeeded, failed } = partition(results);

        const outcome = reportFailures({
            succeeded,
            failed,
            infinitive: "apply theme to",
            pastTense: "Applied theme to",
            logLabel: "add",
        });
        if (outcome.kind === "session") {
            // Trigger an auth refresh so the auth state machine moves to
            // loggedOut and the UI bounces to /login.
            dispatch(fetchSession());
        }

        refreshAfterAssignment();
        return { succeeded, failed };
    };

    const removeFromTheme = async (selectedThemeIds, responsesToRemove) => {
        if (!responsesToRemove) {
            toast.warn("No responses to theme!");
            return { succeeded: [], failed: [] };
        }
        if (!selectedThemeIds) {
            toast.warn("No themes to remove!");
            return { succeeded: [], failed: [] };
        }

        const items = [];
        responsesToRemove.forEach((response) => {
            const existingThemeIds = response.themes
                ? Object.keys(response.themes)
                : [];
            const existingSelectedThemes = selectedThemeIds
                .filter((themeId) => existingThemeIds.includes(themeId))
                .map(Number);
            existingSelectedThemes.forEach((themeId) => {
                const path = `/v2/response/answer/${response.answer_nanoid}/themes/${themeId}`;
                items.push({ path });
            });
        });

        if (items.length === 0) {
            toast.info("Selected themes are not applied to these responses.");
            return { succeeded: [], failed: [] };
        }

        const results = await pAllSettled(
            items,
            (item) => fetcherSimple(item.path, "DELETE"),
            DEFAULT_CONCURRENCY,
        );
        const { succeeded, failed } = partition(results);

        const outcome = reportFailures({
            succeeded,
            failed,
            infinitive: "remove theme from",
            pastTense: "Removed theme from",
            logLabel: "remove",
        });
        if (outcome.kind === "session") {
            dispatch(fetchSession());
        }

        refreshAfterAssignment();
        return { succeeded, failed };
    };

    return { addToTheme, removeFromTheme };
}
